// Resumo Detalhado IA - Bloco 10 Aprimorado
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Brain, TrendingUp, AlertTriangle, CheckCircle, Lightbulb,
  Target, Clock, Users, Loader2, ArrowRight
} from 'lucide-react';

const API_URL = window.location.origin;

const TIPO_CONFIG = {
  positivo: { bg: 'bg-green-50 border-green-200', text: 'text-green-700' },
  alerta: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700' },
  critico: { bg: 'bg-red-50 border-red-200', text: 'text-red-700' }
};

const PRIORIDADE_CONFIG = {
  alta: { bg: 'bg-red-100', text: 'text-red-700' },
  media: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  baixa: { bg: 'bg-blue-100', text: 'text-blue-700' }
};

export function ResumoDetalhado({ year, trimestre }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [year, trimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pendencias-academicas/resumo-detalhado?year=${year}&trimestre=${encodeURIComponent(trimestre)}`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { periodo, estatisticas, analises, recomendacoes, alertas_ativos, tipos_atrasados } = data;

  return (
    <div className="space-y-6" data-testid="resumo-detalhado">
      {/* Header com IA */}
      <Card className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-500 text-white border-0 shadow-lg">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-4 bg-white/20 rounded-2xl">
              <Brain className="w-10 h-10" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Análise Inteligente</h2>
              <p className="text-purple-200">{trimestre} {year} - Visão Completa</p>
            </div>
          </div>
          
          {/* Timeline do Período */}
          <div className="bg-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-purple-200">Progresso do Período</span>
              <span className="font-bold">{periodo.percentual_tempo}% do tempo</span>
            </div>
            <Progress value={periodo.percentual_tempo} className="h-3 bg-white/20 [&>div]:bg-white" />
            <div className="flex justify-between text-xs text-purple-200 mt-2">
              <span>{periodo.dias_decorridos} dias decorridos</span>
              <span>{periodo.dias_totais - periodo.dias_decorridos} dias restantes</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs Comparativos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-purple-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Conclusão</div>
                <div className="text-3xl font-bold text-purple-600">{estatisticas.percentual_conclusao}%</div>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <Progress value={estatisticas.percentual_conclusao} className="h-2 mt-3" />
          </CardContent>
        </Card>
        
        <Card className="border-red-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Críticas</div>
                <div className="text-3xl font-bold text-red-600">{estatisticas.pendencias_criticas}</div>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Alertas</div>
                <div className="text-3xl font-bold text-orange-600">{alertas_ativos}</div>
              </div>
              <div className="p-3 bg-orange-100 rounded-xl">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-100">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-500">Turmas OK</div>
                <div className="text-3xl font-bold text-green-600">
                  {estatisticas.turmas_completas}/{estatisticas.total_turmas}
                </div>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Análises da IA */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Análises Automáticas
          </CardTitle>
          <CardDescription>
            Insights gerados pela IA com base nos dados atuais
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analises.map((analise, idx) => {
              const config = TIPO_CONFIG[analise.tipo] || TIPO_CONFIG.alerta;
              
              return (
                <div key={idx} className={`p-4 rounded-xl border-2 ${config.bg}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{analise.icone}</span>
                    <div>
                      <h4 className={`font-semibold ${config.text}`}>{analise.titulo}</h4>
                      <p className={`text-sm mt-1 ${config.text} opacity-80`}>{analise.descricao}</p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {analises.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Tudo em ordem! Nenhuma análise crítica no momento.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recomendações */}
      {recomendacoes.length > 0 && (
        <Card className="border-indigo-100 bg-indigo-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-indigo-600" />
              Recomendações
            </CardTitle>
            <CardDescription>
              Ações sugeridas pela IA para melhorar a situação
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recomendacoes.map((rec, idx) => {
                const config = PRIORIDADE_CONFIG[rec.prioridade] || PRIORIDADE_CONFIG.media;
                
                return (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-white rounded-lg border">
                    <Badge className={`${config.bg} ${config.text}`}>
                      {rec.prioridade}
                    </Badge>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{rec.acao}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Métricas de Atendimento */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-base">Por Turma</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Completas</span>
                  <span className="font-medium">{estatisticas.turmas_completas}</span>
                </div>
                <Progress value={(estatisticas.turmas_completas / estatisticas.total_turmas) * 100} className="h-2" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-700">
                  {Math.round((estatisticas.turmas_completas / estatisticas.total_turmas) * 100)}%
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Com pendência</span>
                <span className="font-medium text-orange-600">{estatisticas.turmas_com_pendencia}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-base">Por Professor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Com pendências</span>
                <Badge className="bg-orange-100 text-orange-700">
                  {estatisticas.professores_com_pendencia}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Disciplinas afetadas</span>
                <Badge className="bg-purple-100 text-purple-700">
                  {estatisticas.disciplinas_com_pendencia}
                </Badge>
              </div>
              {tipos_atrasados > 0 && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-slate-600">Tipos em atraso</span>
                  <Badge className="bg-red-100 text-red-700">
                    {tipos_atrasados}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
